<template>
  <div class="dashboard-container">
    <h1 class="page-title center">My Applications</h1>
    <p class="page-subtitle center">
      Track your job applications and their current status
    </p>

    <div v-if="loading" class="state-message">Loading your applications…</div>
    <div v-else-if="apps.length === 0" class="state-message">
      You haven’t submitted any applications yet.
    </div>

    <div v-else class="table-container">
      <table class="apps-table">
        <thead>
          <tr>
            <th>Position</th>
            <th>Date Applied</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="app in apps" :key="app.id">
            <td>{{ app.positionapplying || app.position }}</td>
            <td>{{ formatDate(app.submittedAt) }}</td>
            <td>
              <span :class="statusClass(app.status)">
                {{ app.status }}
              </span>
            </td>
            <td>
              <router-link
                :to="{ name: 'ApplicationDetail', params: { id: app.id } }"
                class="view-button"
              >
                View Details
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { auth, db } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs
} from 'firebase/firestore'

const apps = ref([])
const loading = ref(true)
const router = useRouter()

function formatDate(timestamp) {
  if (!timestamp) return '—'
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

function statusClass(status) {
  return {
    'status-pill': true,
    'status-pending':  status.toLowerCase() === 'pending',
    'status-approved': status.toLowerCase() === 'approved',
    'status-rejected': status.toLowerCase() === 'rejected'
  }
}

onMounted(() => {
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      return router.push({ name: 'Login', query: { redirect: '/my-applications' } })
    }

    const appsRef = collection(db, 'applications')
    const q = query(
      appsRef,
      where('email', '==', user.email),
      orderBy('submittedAt', 'desc')
    )

    const snap = await getDocs(q)
    // Applications an admin removed stay in the database but are hidden from the applicant
    apps.value = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => !a.removed)
    loading.value = false
  })
})
</script>
<style scoped>
.dashboard-container {
  max-width: 900px;
  margin: 3rem auto;
  padding: 0 1rem;
}
.page-title {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: #111827; /* gray-900 */
}
.page-subtitle {
  font-size: 1.125rem;
  margin-bottom: 2rem;
  color: #6B7280; /* gray-500 */
}
.center {
  text-align: center;
}
.state-message {
  text-align: center;
  color: #6B7280;
  padding: 2rem 0;
}

/* Wrap in a soft card */
.table-container {
  background: #FFFFFF;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow-x: auto;
}

/* Base table styles */
.apps-table {
  width: 100%;
  border-collapse: collapse;
}
.apps-table th,
.apps-table td {
  padding: 1rem;
  text-align: left;
}

/* Header row */
.apps-table thead {
  background: #F3F4F6; /* gray-100 */
}
.apps-table thead th {
  color: #374151;      /* gray-700 */
  font-size: 0.875rem; /* sm */
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-bottom: 2px solid #E5E7EB; /* gray-200 */
}

/* Row striping + hover */
.apps-table tbody tr:nth-child(even) {
  background: #F9FAFB; /* gray-50 */
}
.apps-table tbody tr:hover {
  background: #EFF6FF; /* light indigo tint */
}

/* Cell borders */
.apps-table td {
  border-bottom: 1px solid #E5E7EB; /* gray-200 */
  color: #1F2937; /* gray-800 */
}

/* Status pills */
.status-pill {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;  /* xs */
  font-weight: bolder;
  text-transform: uppercase;
  color: #000000;
}
.status-pending  { background: #F59E0B; } /* amber-500 */
.status-approved { background: #10B981; } /* emerald-500 */
.status-rejected { background: #EF4444; } /* red-500 */

/* View button */
.view-button {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  background-color: #4F46E5; /* indigo-600 */
  color: #FFFFFF;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s ease;
}
.view-button:hover {
  background-color: #4338CA; /* indigo-700 */
}
</style>
